import { useEffect, useRef } from "react";
import { motion } from "motion/react";

const FIRE_URL = "/video/fire.mp4";
const LOVE_URL = "/video/love.mp4";

export interface ModeVideoBackgroundProps {
  /** Slider position, -1 (full toast) .. 1 (full roast). 0 = neutral. */
  pos: number;
}

/** Two-video (fire/heart) crossfade background driven by slider `pos`, over a dark base. */
export function ModeVideoBackground({ pos }: ModeVideoBackgroundProps) {
  const fireRef = useRef<HTMLVideoElement>(null);
  const loveRef = useRef<HTMLVideoElement>(null);
  const crackleRef = useRef<HTMLAudioElement>(null);
  const toastRef = useRef<HTMLAudioElement>(null);
  const posRef = useRef(pos);
  posRef.current = pos;

  // Direct CDN srcs, deliberately: a blob-fetch + src-swap "insurance" layer was
  // tried here and REVERTED — on a warm cache the fetch resolved before playback
  // started, the swap fired, and blob-src <video> playback proved unreliable on a
  // real iPhone (fresh load played, refresh went black — a perfect cold/warm A/B).
  // Delivery is verified healthy (206 on every Range shape, both files, cold and
  // warm CDN edges) and /video/** carries an explicit cache policy in firebase.json;
  // recovery from transient autoplay blocks is the resync triggers below.

  // One idempotent sync: play/pause every layer to match the current pos. play()
  // failures are swallowed by design (never crash the menu) — which is exactly why
  // recovery has to come from re-running this on every trigger below, not from a
  // one-shot attempt: iOS Low Power Mode rejects even muted autoplay until a tap,
  // and backgrounding pauses media with no per-element event.
  const syncPlayback = () => {
    const fire = fireRef.current;
    const love = loveRef.current;
    const crackle = crackleRef.current;
    const toastBg = toastRef.current;
    if (!fire || !love) return;
    const p = posRef.current;
    if (p > 0) {
      // fire showing → ambient crackle
      fire.play()?.catch(() => {});
      love.pause();
      crackle?.play()?.catch(() => {});
      toastBg?.pause();
    } else if (p < 0) {
      // love (toast) showing → toast background track
      love.play()?.catch(() => {});
      fire.pause();
      toastBg?.play()?.catch(() => {});
      crackle?.pause();
    } else {
      fire.pause();
      love.pause();
      crackle?.pause();
      toastBg?.pause();
    }
  };
  const syncRef = useRef(syncPlayback);
  syncRef.current = syncPlayback;

  useEffect(() => {
    if (crackleRef.current) crackleRef.current.volume = 0.35;
    if (toastRef.current) toastRef.current.volume = 0.35;
    syncRef.current();
  }, [pos]);

  // Recovery triggers (persistent): any tap re-kicks blocked media (audio gesture
  // unlock + iOS Low Power Mode video autoplay); returning from the background
  // resumes what iOS silently paused. Re-syncing already-playing media is a no-op.
  useEffect(() => {
    const resync = () => syncRef.current();
    const onVisible = () => { if (document.visibilityState === "visible") syncRef.current(); };
    window.addEventListener("pointerdown", resync);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("pointerdown", resync);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return (
    <div className="absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-black" />
      {/* initial={false}: /create fully remounts on every navigation back, and without a
          pinned mount state motion tweens opacity 0→1 (~400ms) each time — the active
          video played invisibly and any playback hiccup read as stuck black. */}
      <motion.video
        ref={fireRef}
        data-testid="bg-fire"
        src={FIRE_URL}
        preload="auto"
        loop
        muted
        playsInline
        initial={false}
        animate={{ opacity: Math.max(0, pos) }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <motion.video
        ref={loveRef}
        data-testid="bg-love"
        src={LOVE_URL}
        preload="auto"
        loop
        muted
        playsInline
        initial={false}
        animate={{ opacity: Math.max(0, -pos) }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <audio ref={crackleRef} data-testid="bg-crackle" src="/audio/fire_crackle.m4a" loop preload="auto" />
      <audio ref={toastRef} data-testid="bg-toast-audio" src="/audio/toast_registration_bg.aac" loop preload="auto" />
    </div>
  );
}
